import { useEffect, useState } from "react";
import { CalendarClock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGENT_SETTINGS } from "@/constants/testIds";
import api from "@/lib/api";

/** Configuracion del flujo de agendamiento automatico del agente: activar/
 * desactivar el agendamiento, elegir que servicios requieren siempre
 * atencion humana, sena/pago anticipado y parametros de disponibilidad. */
export default function SchedulingTab({ config, onSave }) {
  const [form, setForm] = useState(null);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        booking_enabled: config.booking_enabled ?? false,
        booking_link_enabled: config.booking_link_enabled ?? false,
        always_human_service_ids: config.always_human_service_ids || [],
        deposit_enabled: config.deposit_enabled ?? false,
        deposit_type: config.deposit_type || "percentage",
        deposit_value: config.deposit_value ?? 30,
        booking_lead_minutes: config.booking_lead_minutes ?? "",
        booking_slot_options_count: config.booking_slot_options_count ?? 3,
      });
    }
  }, [config]);

  useEffect(() => {
    api.get("/services").then((res) => setServices(res.data)).catch(() => setServices([]));
  }, []);

  if (!form) return <p className="text-sm text-muted-foreground">Cargando...</p>;

  const toggleAlwaysHuman = (serviceId) => {
    setForm((f) => {
      const current = f.always_human_service_ids;
      const next = current.includes(serviceId) ? current.filter((id) => id !== serviceId) : [...current, serviceId];
      return { ...f, always_human_service_ids: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        booking_enabled: form.booking_enabled,
        booking_link_enabled: form.booking_link_enabled,
        always_human_service_ids: form.always_human_service_ids,
        deposit_enabled: form.deposit_enabled,
        deposit_type: form.deposit_type,
        deposit_value: Number(form.deposit_value) || 0,
        booking_lead_minutes: form.booking_lead_minutes === "" ? null : Number(form.booking_lead_minutes),
        booking_slot_options_count: Number(form.booking_slot_options_count) || 3,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <CalendarClock className="w-5 h-5 text-brand mt-0.5" />
          <div>
            <Label className="text-sm font-medium text-foreground">Agendamiento automatico</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Cuando esta activo, el agente puede consultar disponibilidad real y crear turnos directamente desde WhatsApp.
              Desactivado: solo informa y deriva al equipo para coordinar el turno.
            </p>
          </div>
        </div>
        <Switch checked={form.booking_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, booking_enabled: v }))} data-testid={AGENT_SETTINGS.schedulingEnabledToggle} />
      </div>

      <div className="bg-white border border-border rounded-xl p-4 flex items-center justify-between gap-4">
        <div>
          <Label className="text-sm font-medium text-foreground">Link de reserva para el lead</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Cuando el lead quiere reservar, el agente le manda un link a la página de reservas con el tratamiento y sus datos ya cargados: elige día y horario y paga si corresponde.
            El turno queda en la agenda y el chat pasa a "Cita confirmada". Antes de activarlo, cargá los horarios de cada profesional.
            {form.booking_enabled ? " No se usa mientras el agendamiento automático esté activo." : ""}
          </p>
        </div>
        <Switch checked={form.booking_link_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, booking_link_enabled: v }))} data-testid={AGENT_SETTINGS.bookingLinkToggle} />
      </div>

      <div>
        <Label className="text-sm text-muted-foreground mb-1 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Servicios que siempre requieren atencion humana</Label>
        <p className="text-xs text-muted-foreground mb-2">Los tildados nunca se agendan solos: el agente escala la conversacion al equipo para coordinarlos personalmente (ej. tratamientos complejos o de alto ticket).</p>
        <div className="bg-white border border-border rounded-xl divide-y divide-border max-h-64 overflow-y-auto">
          {services.length === 0 && <p className="text-sm text-muted-foreground p-4">No hay servicios cargados todavia.</p>}
          {services.map((s) => (
            <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/40" data-testid={`${AGENT_SETTINGS.schedulingServiceRowPrefix}${s.id}`}>
              <Checkbox
                checked={form.always_human_service_ids.includes(s.id)}
                onCheckedChange={() => toggleAlwaysHuman(s.id)}
                data-testid={`${AGENT_SETTINGS.schedulingServiceHumanCheckboxPrefix}${s.id}`}
              />
              <span className="text-sm text-foreground">{s.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-4 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-sm font-medium text-foreground">Pedir sena / pago anticipado</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Se envia el link de cobro de Square junto con la confirmacion del turno.</p>
          </div>
          <Switch checked={form.deposit_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, deposit_enabled: v }))} data-testid={AGENT_SETTINGS.schedulingDepositToggle} />
        </div>
        {form.deposit_enabled && (
          <div className="flex items-center gap-3">
            <Select value={form.deposit_type} onValueChange={(v) => setForm((f) => ({ ...f, deposit_type: v }))}>
              <SelectTrigger className="w-40" data-testid={AGENT_SETTINGS.schedulingDepositTypeSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                <SelectItem value="fixed">Monto fijo (USD)</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="number" min="0" step="0.01" className="w-32"
              value={form.deposit_value}
              onChange={(e) => setForm((f) => ({ ...f, deposit_value: e.target.value }))}
              data-testid={AGENT_SETTINGS.schedulingDepositValueInput}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Anticipacion minima (min)</Label>
          <Input
            type="number" min="0" placeholder="Usa el de la pagina publica"
            value={form.booking_lead_minutes}
            onChange={(e) => setForm((f) => ({ ...f, booking_lead_minutes: e.target.value }))}
            data-testid={AGENT_SETTINGS.schedulingLeadMinutesInput}
          />
        </div>
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Opciones de horario a ofrecer</Label>
          <Input
            type="number" min="1" max="6"
            value={form.booking_slot_options_count}
            onChange={(e) => setForm((f) => ({ ...f, booking_slot_options_count: e.target.value }))}
            data-testid={AGENT_SETTINGS.schedulingSlotOptionsInput}
          />
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={AGENT_SETTINGS.saveSchedulingButton}>
        {saving ? "Guardando..." : "Guardar agendamiento"}
      </Button>
    </div>
  );
}
